import { RefObject, useEffect, useState } from 'react';

/**
 * How far the hero has travelled through the viewport: 0 as its top meets the foot of the
 * screen, 1 once its bottom has cleared the head of it. `null` until it has been measured.
 *
 * headless drove the hero's layers with react-scroll-parallax's `<ParallaxBanner>`, which
 * works out this one progress from the banner itself and hands it to every layer, so they
 * all move in step whatever their own height. That library is not a dependency here and
 * adding one for a single decorative effect is a poor trade: it is a client-only provider,
 * and this page is server-rendered and cached for a day, so it would have to agree with the
 * server about a transform on first paint or throw the whole render away.
 *
 * `null` is that agreement. The server has no viewport to measure against, so it emits no
 * transform at all, and neither does the browser's first render — which is exactly the
 * markup react-scroll-parallax left behind until its own effect ran there.
 *
 * Reads are rAF-throttled because `scroll` fires far faster than the compositor can use,
 * and a visitor who has asked for less motion gets none: progress stays `null` and the
 * layers sit still, spread as their own boxes place them.
 */
export default function useParallax(hero: RefObject<HTMLElement | null>): number | null {
  const [progress, setProgress] = useState<number | null>(null);

  useEffect(() => {
    const el = hero.current;

    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    let frame = 0;

    const measure = () => {
      frame = 0;

      const { top, height } = el.getBoundingClientRect();
      const view = window.innerHeight;

      setProgress(Math.min(Math.max((view - top) / (view + height), 0), 1));
    };

    const remeasure = () => {
      if (frame) {
        return;
      }

      frame = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', remeasure, { passive: true });

    // The hero is a screen minus a navigation that useHeaderHeight has not measured on the
    // first pass, so it is a hundred-odd pixels too tall until a second render corrects it
    // — and nothing scrolls when that happens. Without watching the element the layers keep
    // the progress they were handed before the page had settled, which on a hero this size
    // reads as exactly half way and leaves every one of them sitting undrifted.
    const observer = new ResizeObserver(remeasure);
    observer.observe(el);

    // A phone hiding its address bar changes the viewport this is measured against without
    // necessarily changing `100vh`, so the observer alone would not hear about it.
    window.addEventListener('resize', remeasure);

    return () => {
      window.removeEventListener('scroll', remeasure);
      window.removeEventListener('resize', remeasure);
      observer.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [hero]);

  return progress;
}
